import React, { createContext, useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { normalizePlanRecords } from '../utils/planRecords';
import { normalizeStorageAddonProducts } from '../utils/storageAddons';

const AppContext = createContext();
export const useApp = () => useContext(AppContext);

const mergePublicConfigRows = (mainConfig, appConfig) => {
    if (!mainConfig && !appConfig) return null;

    return {
        ...(mainConfig || {}),
        ...(appConfig || {}),
        data_payload: {
            ...(mainConfig?.data_payload || {}),
            ...(appConfig?.data_payload || {}),
        },
    };
};

const flattenPublicConfig = (configData) => ({
    ...configData,
    ...(configData?.data_payload || {}),
});

const EMPTY_PUBLIC_CONFIG = {
    telegramSalesLink: '',
    helpCenterContactLink: '',
    helpCenterTelegramLink: '',
    educationCategories: [],
    landingResourceLabels: {},
    publicPageCopy: {},
    contact_email: '',
    social_twitter: '',
    social_facebook: '',
    social_telegram: '',
};

// Admin saves invalidate this query immediately in the current session. Other
// sessions refresh on a bounded cadence without polling Supabase in background tabs.
const APP_DATA_STALE_TIME_MS = 30 * 60 * 1000;

const getSupabaseClient = async () => {
    const module = await import('../lib/supabase');
    return module.supabase;
};

export const AppProvider = ({ children }) => {
    const appId = process.env.REACT_APP_PROJECT_ID || process.env.NEXT_PUBLIC_REACT_APP_PROJECT_ID || 'trader-journal';
    const {
        data: bootstrapData,
        isLoading: isLoadingPublicConfig,
    } = useQuery({
        queryKey: ['appBootstrap', appId],
        queryFn: async () => {
            const supabase = await getSupabaseClient();
            const { data, error } = await supabase.rpc('get_public_app_bootstrap', {
                p_app_id: appId,
            });

            if (error) throw error;
            return data || {};
        },
        enabled: Boolean(appId),
        staleTime: APP_DATA_STALE_TIME_MS,
        refetchInterval: APP_DATA_STALE_TIME_MS,
        refetchIntervalInBackground: false,
        gcTime: 60 * 60 * 1000,
    });

    const configData = useMemo(() => mergePublicConfigRows(
        bootstrapData?.main_config,
        bootstrapData?.app_config
    ), [bootstrapData?.app_config, bootstrapData?.main_config]);
    const plans = useMemo(
        () => normalizePlanRecords(bootstrapData?.plans || []),
        [bootstrapData?.plans]
    );
    const storageAddonProducts = useMemo(
        () => normalizeStorageAddonProducts(bootstrapData?.storage_addon_products || []),
        [bootstrapData?.storage_addon_products]
    );
    const masterFeatures = useMemo(
        () => bootstrapData?.master_features || [],
        [bootstrapData?.master_features]
    );
    const internalContracts = useMemo(
        () => bootstrapData?.internal_contracts || [],
        [bootstrapData?.internal_contracts]
    );

    const publicConfig = useMemo(() => ({
        ...EMPTY_PUBLIC_CONFIG,
        ...(configData ? flattenPublicConfig(configData) : {}),
    }), [configData]);

    const value = useMemo(() => ({
        plans,
        storageAddonProducts,
        publicConfig,
        masterFeatures,
        internalContracts,
        appId,
        isPublicConfigReady: !isLoadingPublicConfig,
    }), [appId, internalContracts, isLoadingPublicConfig, masterFeatures, plans, publicConfig, storageAddonProducts]);

    return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
